/**
 * Thin API client for the Thunder public endpoints.
 *
 * In development, requests go to a relative `/api/v1` path which CRA's dev
 * server proxies to the backend (see `proxy` in package.json) — this sidesteps
 * CORS. In production, set REACT_APP_API_URL to the deployed API base.
 *
 * Every reader returns live data on success and `null` on any failure, so the
 * caller can fall back to bundled content and the page always renders.
 */

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

async function getJSON(path, { signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  const body = await res.json();
  // Standard envelope: { success, data, meta? }
  if (!body || body.success !== true) throw new Error('Unexpected response shape');
  return body.data;
}

/** Fetch the public services list, or null on failure. */
export async function fetchServices(opts) {
  try {
    return await getJSON('/services?limit=50', opts);
  } catch {
    return null;
  }
}

/** Fetch the public portfolio list, or null on failure. */
export async function fetchPortfolio(opts) {
  try {
    return await getJSON('/portfolio?limit=50', opts);
  } catch {
    return null;
  }
}

/**
 * Submit a contact/lead. `payload` matches the backend lead schema:
 * { name, email, phone?, subject?, message, website? (honeypot) }.
 * Returns { ok: true } on 201, or { ok: false, error } otherwise.
 */
export async function submitLead(payload) {
  try {
    const res = await fetch(`${API_BASE}/leads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => null);
    if (res.ok && body && body.success) return { ok: true };
    const message =
      (body && body.error && body.error.message) ||
      'Something went wrong sending your message. Please try again.';
    return { ok: false, error: message };
  } catch {
    return {
      ok: false,
      error: 'Could not reach the server. Please check your connection and try again.',
    };
  }
}
